import type { RegulatoryReference } from '../../lib/assessment/types'

/**
 * Static reference library. Applicability is preliminary and source-linked.
 * Where instruments, designation or thresholds need confirmation, rules return
 * further_verification_required rather than definitive obligations.
 */
export const REGULATORY_SOURCES: RegulatoryReference[] = [
  {
    id: 'uae_fdl_11_2024',
    title: 'Federal Decree-Law No. 11 of 2024 on the Reduction of Climate Change Effects',
    authority: 'United Arab Emirates (federal) — Ministry of Climate Change and Environment context',
    url: 'https://uaelegislation.gov.ae/en/legislations/2558',
    jurisdiction: 'UAE (including free zones)',
    publicationDate: '2024-08-28',
    effectiveDate: '2025-05-30',
    lastReviewed: '2026-03-23',
    statusNotes:
      'Federal climate framework. Official English portal text states the Decree-Law applies to sources in the State, including free zones. Detailed measurement, inventory and periodic reporting duties are linked to sources determined by the Ministry and the competent authority. This tool never treats UAE presence alone as confirmed MRV designation.',
    type: 'legislation',
    relatedTopics: ['climate_ghg', 'energy', 'data_governance', 'regulatory_management'],
  },
  {
    id: 'moccae_portal',
    title: 'Ministry of Climate Change and Environment (MOCCAE) — climate / environment authority portal',
    authority: 'Ministry of Climate Change and Environment (UAE)',
    url: 'https://www.moccae.gov.ae/',
    jurisdiction: 'UAE federal',
    lastReviewed: '2026-03-23',
    statusNotes:
      'Authority portal for climate and environment policy context. Use to locate current guidance and implementing instruments. Portal presence is not itself an entity-specific obligation.',
    type: 'regulator_guidance',
    relatedTopics: ['climate_ghg', 'regulatory_management'],
  },
  {
    id: 'dfm_esg_guide',
    title: 'Dubai Financial Market ESG Reporting Guide',
    authority: 'Dubai Financial Market (DFM)',
    url: 'https://www.dfm.ae/discover-dfm/esg-sustainability',
    jurisdiction: 'Dubai / DFM-listed issuers',
    lastReviewed: '2026-03-23',
    statusNotes:
      'Exchange sustainability guidance for DFM context. Confirm current guide version and issuer expectations on the official DFM site.',
    type: 'exchange_guidance',
    relatedTopics: ['climate_ghg', 'ethics', 'data_governance', 'board_oversight'],
  },
  {
    id: 'adx_esg_overview',
    title: 'Abu Dhabi Securities Exchange Sustainability / ESG disclosure guidance',
    authority: 'Abu Dhabi Securities Exchange (ADX)',
    url: 'https://www.adx.ae/about-adx/sustainability/overview',
    jurisdiction: 'Abu Dhabi / ADX-listed issuers',
    lastReviewed: '2026-03-23',
    statusNotes:
      'ADX sustainability overview and related issuer guidance. Verify current requirements and metric expectations on the official ADX pages.',
    type: 'exchange_guidance',
    relatedTopics: ['climate_ghg', 'data_governance', 'board_oversight'],
  },
  {
    id: 'adx_esg_metrics',
    title: 'ADX ESG Metrics',
    authority: 'Abu Dhabi Securities Exchange (ADX)',
    url: 'https://www.adx.ae/about-adx/sustainability/esg-metrics',
    jurisdiction: 'Abu Dhabi / ADX-listed issuers',
    lastReviewed: '2026-03-23',
    statusNotes:
      'Metric reference for ADX ESG disclosure context. Confirm metric list and applicability for your issuer category.',
    type: 'exchange_guidance',
    relatedTopics: ['climate_ghg', 'energy', 'water', 'ohs', 'ethics'],
  },
  {
    id: 'ghg_protocol',
    title: 'GHG Protocol Corporate Accounting and Reporting standards',
    authority: 'World Resources Institute / World Business Council for Sustainable Development',
    url: 'https://ghgprotocol.org/',
    jurisdiction: 'Voluntary international methodology',
    lastReviewed: '2026-03-23',
    statusNotes:
      'Widely used GHG accounting methodology. Not UAE legislation. Often referenced by customers, inventories and assurance readiness work.',
    type: 'voluntary_framework',
    relatedTopics: ['climate_ghg', 'energy', 'data_governance'],
  },
  {
    id: 'gri_standards',
    title: 'GRI Standards',
    authority: 'Global Reporting Initiative',
    url: 'https://www.globalreporting.org/standards/',
    jurisdiction: 'Voluntary international framework',
    lastReviewed: '2026-03-23',
    statusNotes:
      'Voluntary reporting framework. Not UAE legislation. May be relevant where adopted by the company or requested by stakeholders.',
    type: 'voluntary_framework',
    relatedTopics: ['climate_ghg', 'energy', 'water', 'waste', 'ohs', 'labour', 'ethics'],
  },
  {
    id: 'ifrs_sustainability',
    title: 'IFRS Sustainability Disclosure Standards (ISSB)',
    authority: 'IFRS Foundation / ISSB',
    url: 'https://www.ifrs.org/issued-standards/ifrs-sustainability-standards-navigator/',
    jurisdiction: 'Voluntary / jurisdiction-dependent adoption',
    lastReviewed: '2026-03-23',
    statusNotes:
      'International sustainability disclosure standards. Not automatically UAE law. Relevance depends on listing, parent requirements and local adoption decisions.',
    type: 'voluntary_framework',
    relatedTopics: ['climate_ghg', 'climate_adaptation', 'data_governance', 'risk_management'],
  },
  {
    id: 'customer_supply_chain_esg',
    title: 'Customer and supply-chain ESG disclosure expectations',
    authority: 'Market / contractual counterparties',
    url: 'https://ecopshub.com/tools/uae-sustainability-assessment/privacy',
    jurisdiction: 'Contractual / market-driven',
    lastReviewed: '2026-03-23',
    statusNotes:
      'Not a statute. Captures common customer, lender and multinational cascade requests for ESG or carbon data.',
    type: 'customer_expectation',
    relatedTopics: ['climate_ghg', 'procurement', 'data_governance', 'human_rights'],
  },
  {
    id: 'lender_esg_expectations',
    title: 'Lender and banking ESG information requests',
    authority: 'Market / financial counterparties',
    url: 'https://ecopshub.com/tools/uae-sustainability-assessment/privacy',
    jurisdiction: 'Contractual / market-driven',
    lastReviewed: '2026-03-23',
    statusNotes:
      'Not UAE legislation. Reflects common lender due-diligence and sustainable-finance information requests.',
    type: 'customer_expectation',
    relatedTopics: ['climate_ghg', 'risk_management', 'ethics', 'data_governance'],
  },
  {
    id: 'eu_cbam_market',
    title: 'EU CBAM-related customer / export disclosure expectations',
    authority: 'European Union CBAM (market exposure for exporters)',
    url: 'https://taxation-customs.ec.europa.eu/carbon-border-adjustment-mechanism_en',
    jurisdiction: 'EU market exposure (not UAE law)',
    lastReviewed: '2026-03-23',
    statusNotes:
      'EU instrument that can create data requests for UAE exporters via customers or importers. Not a UAE statute. Product coverage and roles require case-specific verification.',
    type: 'customer_expectation',
    relatedTopics: ['climate_ghg', 'resource_use', 'data_governance'],
  },
]
